import { Controller } from '@hotwired/stimulus';
import { characterLayout } from '../../lib/character_layout.js';

// Position the existing cards without changing the roster's space or the map's size.
export default class extends Controller {
  static values = { characterLayout: { type: String, default: 'top' }, combat: Boolean };

  connect() {
    this.boundsElement = this.element.parentElement;
    this.cards = new Set();
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.element, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    this.layoutSettled = event => {
      if (event.target === this.screen || event.target === this.element ||
          event.target.classList.contains('game-screen-character-list__cards')) this.schedule();
    };
    this.screen.addEventListener('transitionend', this.layoutSettled);
    this.resizeObserver = new ResizeObserver(() => this.schedule());
    this.resizeObserver.observe(this.element);
    this.resizeObserver.observe(this.screen);
    this.schedule();
  }

  // Read the available content bounds; all mutations stay inside this component.
  get screen() { return this.boundsElement || this.element.parentElement; }

  characterLayoutValueChanged() { this.schedule(); }
  combatValueChanged() {
    this.dispatch('combat-changed');
    this.schedule();
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.render());
  }

  preservePosition(event) {
    if ((this.cards?.has(event.target) && event.detail.attributeName === 'style') ||
        (event.target === this.element && event.detail.attributeName === 'data-character-layout-positioned')) {
      event.preventDefault();
    }
  }

  render() {
    if (!this.element.isConnected) return;
    const characters = [...this.element.querySelectorAll(':scope > .game-screen-character-list__cards > .character_wrapper:not(.hidden_character)')]
      .sort((a, b) => Number(a.style.getPropertyValue('--order')) - Number(b.style.getPropertyValue('--order')));
    const requestedFocus = characters.find(character => character.classList.contains('focused'));
    // Keep the latest request in the DOM while the previous focus returns home.
    // Decide before positioning any card, regardless of its order in the roster.
    const waitingForReturn = characters.some(character => {
      const card = character.querySelector('.character.card');
      return card.style.zIndex === '20' &&
        (character !== requestedFocus || this.returningCards?.has(card));
    });
    const focus = waitingForReturn ? undefined : requestedFocus;
    clearTimeout(this.restoreOverflow);
    if (requestedFocus || (!this.combatValue && this.characterLayoutValue !== 'top')) {
      this.element.dataset.characterLayoutPositioned = '';
    } else {
      // Keep returning cards visible until they reach the scrollable top roster.
      this.restoreOverflow = setTimeout(() => delete this.element.dataset.characterLayoutPositioned, 325);
    }
    const positions = characterLayout(this.characterLayoutValue, characters.length, this.screen.clientWidth, this.screen.clientHeight);
    const current = new Set();
    characters.forEach((character, index) => {
      const card = character.querySelector('.character.card');
      current.add(card);
      if (!this.cards?.has(card)) this.resizeObserver?.observe(card);
      if ((this.combatValue || this.characterLayoutValue === 'top') && character !== focus) {
        this.resetPosition(card);
        this.updateStacking(card, false, false);
        return;
      }
      const height = Math.min(this.screen.clientHeight * 0.6, this.screen.clientWidth * 0.9);
      const box = character === focus
        ? { x: this.screen.clientWidth / 2, y: this.screen.clientHeight / 2, width: height * 2 / 3 }
        : positions[index];
      this.positionCard(card, box);
      this.updateStacking(card, character === focus, true);
    });
    for (const card of this.cards || []) {
      if (current.has(card)) continue;
      this.resizeObserver?.unobserve(card);
      this.resetCard(card);
    }
    this.cards = current;
  }

  positionCard(card, box) {
    if (!card.offsetWidth) return;
    const computed = getComputedStyle(card);
    const previous = { translate: computed.translate, scale: computed.scale };
    card.style.transition = 'none';
    card.style.translate = 'none';
    card.style.scale = '1';
    const origin = card.getBoundingClientRect();
    const screen = this.screen.getBoundingClientRect();
    const screenAngle = this.rotation(this.screen);
    const x = box.x - this.screen.clientWidth / 2;
    const y = box.y - this.screen.clientHeight / 2;
    const dx = screen.left + screen.width / 2 + x * Math.cos(screenAngle) - y * Math.sin(screenAngle) - (origin.left + origin.width / 2);
    const dy = screen.top + screen.height / 2 + x * Math.sin(screenAngle) + y * Math.cos(screenAngle) - (origin.top + origin.height / 2);
    const angle = this.rotation(card.parentElement);
    Object.assign(card.style, previous);
    // Commit the previous position before starting a new CSS transition.
    card.getBoundingClientRect();
    card.style.transition = '';
    card.style.translate = `${dx * Math.cos(angle) + dy * Math.sin(angle)}px ${-dx * Math.sin(angle) + dy * Math.cos(angle)}px`;
    card.style.scale = `${box.width / card.offsetWidth}`;
  }

  updateStacking(card, focused, positioned) {
    this.returningCards ||= new Map();
    const zIndex = focused ? '20' : positioned ? '10' : '';
    this.returningCards.delete(card);
    if (focused || card.style.zIndex !== '20') {
      card.style.zIndex = zIndex;
      return;
    }

    // Read after positioning so the browser starts the new CSS transitions.
    const animations = card.getAnimations().filter(animation =>
      ['translate', 'scale'].includes(animation.transitionProperty));
    if (!animations.length) {
      card.style.zIndex = zIndex;
      this.schedule();
      return;
    }

    const returning = {};
    this.returningCards.set(card, returning);
    return Promise.allSettled(animations.map(animation => animation.finished)).then(() => {
      // A morph, refocus, or removal can supersede this return animation.
      if (this.returningCards.get(card) !== returning) return;
      this.returningCards.delete(card);
      card.style.zIndex = zIndex;
      this.schedule();
    });
  }

  rotation(element) {
    let angle = 0;
    for (; element; element = element.parentElement) {
      const transform = getComputedStyle(element).transform;
      if (transform === 'none') continue;
      const matrix = new DOMMatrixReadOnly(transform);
      angle += Math.atan2(matrix.b, matrix.a);
    }
    return angle;
  }

  resetPosition(card) {
    for (const property of ['translate', 'scale', 'transition']) card.style.removeProperty(property);
  }

  resetCard(card) {
    this.returningCards?.delete(card);
    this.resetPosition(card);
    card.style.removeProperty('z-index');
  }

  clear() {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.restoreOverflow);
    delete this.element.dataset.characterLayoutPositioned;
    for (const card of this.cards || []) this.resetCard(card);
    this.cards = new Set();
  }

  disconnect() {
    this.screen.removeEventListener('transitionend', this.layoutSettled);
    this.observer.disconnect();
    this.resizeObserver.disconnect();
    this.clear();
  }
}
